import {FormattedMessage} from '@pinecast/i18n';
import styled, {useCSS} from '@pinecast/styles';
import * as React from 'react';

import Button from './Button';
import {VerticalGroup} from './Group';
import {useIsDarkMode} from './hooks/useIsDarkMode';
import {ErrorIcon} from './icons';
import {DecorativeImage} from './Image';

const TitleWrapper = styled('h2', (_, designColors) => ({
  color: designColors.headingBlue,
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 14,
  fontWeight: 400,
  marginBottom: 0,
  marginTop: 0,
  textAlign: 'center',
}));
const TextWrapper = styled('div', (_, designColors) => ({
  color: designColors.contentTextAccent,
  fontSize: 13,
  lineHeight: 16,
  textAlign: 'center',
}));

/**
 * The illustration of an error, in the colors of the current mode. It is
 * decorative: the heading beside it says what went wrong. It shrinks to fit a
 * narrow screen, where its 385 pixels would make the page scroll sideways
 * (WCAG 1.4.10).
 */
export const ErrorImage = () => {
  const css = useCSS();
  const darkMode = useIsDarkMode();
  return (
    <DecorativeImage
      className={css({height: 'auto', maxWidth: '100%'})}
      src={
        darkMode
          ? '/static/img/dashboard/error.dark.png'
          : '/static/img/dashboard/error.png'
      }
      height={300}
      width={385.5}
    />
  );
};

const ErrorState = ({
  actionLabel,
  'data-focus-fallback': focusFallback,
  onAction,
  title,
  copy,
}: {
  actionLabel?: React.ReactNode;
  // Makes the title take focus when the element that had it is gone (see
  // `FocusRecoveryProvider`), as it is for an error that takes the place of a
  // step.
  'data-focus-fallback'?: boolean | string;
  onAction?: string | (() => void);
  title: React.ReactNode;
  copy?: React.ReactNode;
}) => {
  return (
    <VerticalGroup
      spacing={16}
      centerChildren
      wrapperStyle={{
        maxWidth: 400,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '12px 0',
      }}
    >
      {copy ? <ErrorIcon size={48} /> : <ErrorImage />}
      <TitleWrapper data-focus-fallback={focusFallback || undefined}>
        {title}
      </TitleWrapper>
      {Boolean(copy) && <TextWrapper>{copy}</TextWrapper>}
      {Boolean(onAction) && (
        <Button
          {...(typeof onAction === 'string'
            ? {href: onAction}
            : {onClick: onAction})}
          size="small"
        >
          {actionLabel || (
            <FormattedMessage
              description="Default retry button when an error is shown"
              defaultMessage="Retry"
            />
          )}
        </Button>
      )}
    </VerticalGroup>
  );
};

export default ErrorState;
